import {
    CheckCircle,
    Clock,
    Edit,
    Eye,
    Plus,
    RefreshCw,
    Search,
    Send,
    X,
    XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { api, Center, centerApi, signUploadKeys } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

interface CenterFormData {
    name: string;
    address: string;
    nearbyLandmark: string;
    pincode: string;
    distanceFromCityCenter: 'ZERO_KM' | 'ONE_TO_FIVE_KM' | 'SIX_TO_TEN_KM' | 'ELEVEN_TO_TWENTY_KM' | 'BEYOND_TWENTY_KM';
    geoCoordinates: string;
    cctvFeedLink: string;

    // General Infrastructure Checklist
    general_cleanPremises: boolean;
    general_accessible: boolean;
    general_properCarpetArea: boolean;
    general_separateAreas: boolean;
    general_receptionDesk: boolean;
    general_photographs: string[];

    // Classroom Facilities Checklist
    classroom_dedicatedClassrooms: boolean;
    classroom_whiteboard: boolean;
    classroom_adequateChairs: boolean;
    classroom_trainerTable: boolean;
    classroom_lightingAndVentilation: boolean;
    classroom_fansOrAC: boolean;
    classroom_photographs: string[];

    // IT Infrastructure Checklist
    it_projectorOrTv: boolean;
    it_audioSystem: boolean;
    it_internetSpeed: boolean;
    it_upsBackup: boolean;
    it_photographs: string[];

    // Electrical and Safety Checklist
    electrical_properWiring: boolean;
    electrical_fireExtinguisher: boolean;
    electrical_emergencyExitPlan: boolean;
    electrical_firstAidKit: boolean;
    electrical_photographs: string[];

    // Washrooms and Hygiene Checklist
    washrooms_separateToilets: boolean;
    washrooms_regularCleaning: boolean;
    washrooms_handwashing: boolean;
    washrooms_photographs: string[];

    // Record Keeping and Display Checklist
    recordKeeping_attendanceSystem: boolean;
    recordKeeping_cctvInstalled: boolean;
    recordKeeping_cctvLink: string;
    recordKeeping_photographs: string[];

    // Branding and Signage Checklist
    branding_brandingAtEntrance: boolean;
    branding_posters: boolean;
    branding_photographs: string[];
}

export type UpdateCenterPayload = Omit<CenterFormData, 'center_id' | 'mp_id' | 'status' | 'rejection_reason' | 'created_at' | 'updated_at' | 'mobilisation_partner' | 'approval_history'>;


interface MobilisationPartner {
    mp_id: number;
    name: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    gst_number?: string;
    status: string;
    metadata?: any;
    created_at: string;
    _count?: {
        centers: number;
        trainers: number;
        mobilisations: number;
        users: number;
    };
}

type MultiImageFieldProps = {
    label: string;
    keys: string[]; // S3 keys stored in form
    onChange: (next: string[]) => void;
};

// Put this near your form state definition
const INITIAL_CENTER_FORM: CenterFormData = {
    name: '',
    address: '',
    nearbyLandmark: '',
    pincode: '',
    distanceFromCityCenter: 'ZERO_KM',
    geoCoordinates: '',
    cctvFeedLink: '',

    general_cleanPremises: false,
    general_accessible: false,
    general_properCarpetArea: false,
    general_separateAreas: false,
    general_receptionDesk: false,
    general_photographs: [],

    classroom_dedicatedClassrooms: false,
    classroom_whiteboard: false,
    classroom_adequateChairs: false,
    classroom_trainerTable: false,
    classroom_lightingAndVentilation: false,
    classroom_fansOrAC: false,
    classroom_photographs: [],

    it_projectorOrTv: false,
    it_audioSystem: false,
    it_internetSpeed: false,
    it_upsBackup: false,
    it_photographs: [],

    electrical_properWiring: false,
    electrical_fireExtinguisher: false,
    electrical_emergencyExitPlan: false,
    electrical_firstAidKit: false,
    electrical_photographs: [],

    washrooms_separateToilets: false,
    washrooms_regularCleaning: false,
    washrooms_handwashing: false,
    washrooms_photographs: [],

    recordKeeping_attendanceSystem: false,
    recordKeeping_cctvInstalled: false,
    recordKeeping_cctvLink: '',
    recordKeeping_photographs: [],

    branding_brandingAtEntrance: false,
    branding_posters: false,
    branding_photographs: [],
};

const CENTER_ALLOWED_KEYS = Object.keys(INITIAL_CENTER_FORM) as (keyof CenterFormData)[];

function sanitizeCenterPayload(anyObj: any): UpdateCenterPayload {
    const out: Partial<CenterFormData> = {};
    for (const k of CENTER_ALLOWED_KEYS) {
        const value = (anyObj as any)[k];
        out[k] = typeof value === 'string' ? value.trim() : value;
    }
    return out as UpdateCenterPayload;
}


const labels = {
    // Basic Details
    name: "Center Name",
    address: "Address",
    nearbyLandmark: "Nearby Landmark",
    pincode: "Pin Code",
    distanceFromCityCenter: "How far is this Venue from the city/town center?",
    geoCoordinates: "Geo Coordinates (latitude, longitude)",
    cctvFeedLink: "CCTV Feed Link",

    // General Infrastructure Checklist
    general_cleanPremises: "Clean, well-maintained premises with proper ventilation and lighting",
    general_accessible: "Easily accessible location with signboards indicating “Bajaj SkillServ Center”",
    general_properCarpetArea: "Minimum carpet area as per batch size requirement (e.g., ~600–800 sq. ft. per batch)",
    general_separateAreas: "Separate areas for training, counseling, and administrative functions",
    general_receptionDesk: "Reception/Help Desk with seating for visitors",
    general_photographs: "Upload Photograph/Photographs (General Infrastructure)",

    // Classroom Facilities Checklist
    classroom_dedicatedClassrooms: "Dedicated classroom(s) with capacity for 30–45 students",
    classroom_whiteboard: "Whiteboard/Smartboard and marker kits",
    classroom_adequateChairs: "Adequate student chairs with writing pads/desks",
    classroom_trainerTable: "Trainer table and chair",
    classroom_lightingAndVentilation: "Adequate lighting and ventilation",
    classroom_fansOrAC: "Fans/air-conditioning (depending on location and season)",
    classroom_photographs: "Upload Photograph/Photographs (Classroom Facilities)",

    // IT Infrastructure Checklist
    it_projectorOrTv: "Projector and screen or smart TV for digital learning",
    it_audioSystem: "Functional and clear Audio system",
    it_internetSpeed: "Internet connection – minimum 20 Mbps speed",
    it_upsBackup: "UPS/Inverter backup for minimum 2 hours",
    it_photographs: "Upload Photograph/Photographs (IT Infrastructure)",

    // Electrical and Safety Checklist
    electrical_properWiring: "Proper electrical wiring and fixtures",
    electrical_fireExtinguisher: "Fire extinguisher(s) available and accessible",
    electrical_emergencyExitPlan: "Emergency exit plan visible",
    electrical_firstAidKit: "First aid kit stocked and accessible",
    electrical_photographs: "Upload Photograph/Photographs (Electrical and Safety)",

    // Washrooms and Hygiene Checklist
    washrooms_separateToilets: "Separate toilets for male and female candidates",
    washrooms_regularCleaning: "Regular cleaning and maintenance schedule",
    washrooms_handwashing: "Handwashing and drinking water facility",
    washrooms_photographs: "Upload Photograph/Photographs (Washrooms and Hygiene)",

    // Record Keeping and Display Checklist
    recordKeeping_attendanceSystem: "Biometric or Face Recognition attendance system",
    recordKeeping_cctvInstalled: "CCTV camera(s) installed and functional",
    recordKeeping_cctvLink: "CCTV live streaming link (if available)",
    recordKeeping_photographs: "Upload Photograph/Photographs (Record Keeping and Display)",

    // Branding and Signage Checklist
    branding_brandingAtEntrance: "Bajaj SkillServ branding at the main entrance",
    branding_posters: "Branding posters/banners displayed inside the center",
    branding_photographs: "Upload Photograph/Photographs (Branding and Signage)",
};

const signedUrlCache = new Map<string, string>();
async function getSignedUrls(keys: string[]) {
    const missing = keys.filter(k => !signedUrlCache.has(k));
    if (missing.length) {
        const map = await signUploadKeys(missing);
        Object.entries(map).forEach(([k, url]) => signedUrlCache.set(k, url));
    }
    return keys.map(k => signedUrlCache.get(k)!);
}
function deleteSignedCacheFor(key: string) {
    signedUrlCache.delete(key);
}



function MultiImageField({ label, keys, onChange }: MultiImageFieldProps) {
    const [busy, setBusy] = React.useState(false);
    const [urls, setUrls] = React.useState<string[]>([]);

    React.useEffect(() => {
        (async () => {
            if (!keys?.length) { setUrls([]); return; }
            const u = await getSignedUrls(keys);
            setUrls(u);
        })();
    }, [keys]);

    const removeAt = (idx: number) => {
        const key = keys[idx];
        deleteSignedCacheFor(key);      // 🔑 invalidate preview cache immediately
        const next = [...keys];
        next.splice(idx, 1);
        onChange(next);
    };

    const onSelect: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        try {
            const uploaded: string[] = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append('file', f);
                const resp = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                uploaded.push(resp.data.key);
            }
            onChange([...(keys || []), ...uploaded]);
        } finally {
            setBusy(false);
            // e.currentTarget.value = '';
            e.target.value = '';
        }
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-800">{label}</label>
            <input type="file" multiple accept="image/*" onChange={onSelect} />
            {busy && <div className="text-xs text-gray-500">Uploading…</div>}
            {!!urls.length && (
                <div className="grid grid-cols-3 gap-2">
                    {urls.map((u, i) => (
                        <div key={keys[i]} className="relative group">
                            <img src={u} alt={`${label}-${i}`} className="w-full h-24 object-cover rounded border" />
                            <button
                                type="button"
                                onClick={() => removeAt(i)}
                                className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100"
                                aria-label="Remove"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ImageGridSigned({ keys, title }: { keys?: string[]; title: string }) {
    const safeKeys = Array.isArray(keys) ? keys : [];
    const [urls, setUrls] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!safeKeys.length) {
                setUrls([]);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const signed = await getSignedUrls(safeKeys); // must return URLs in same order as keys
                if (!cancelled) setUrls(signed);
            } catch (e) {
                console.error(e);
                if (!cancelled) setError('Could not load images');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [JSON.stringify(safeKeys)]); // shallow keys → stable dep

    if (!safeKeys.length) return null;

    return (
        <div className="mb-4">
            <div className="font-medium text-gray-800 mb-2">{title}</div>

            {loading && (
                <div className="text-sm text-gray-500 mb-2">Loading…</div>
            )}
            {error && (
                <div className="text-sm text-red-600 mb-2">{error}</div>
            )}

            <div className="grid grid-cols-3 gap-2">
                {urls.map((u, i) => (
                    <a
                        key={safeKeys[i] ?? i}
                        href={u}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                        title="Open full image"
                    >
                        <img
                            src={u}
                            alt={`Attachment ${i + 1}`}
                            className="w-full h-24 object-cover rounded border"
                        />
                    </a>
                ))}
            </div>
        </div>
    );
}


function BooleanChecklist({
    data,
    labels,
    title = 'Checklist',
}: {
    data: Record<string, any>;
    labels: Record<string, string>;
    title?: string;
}) {
    const items = Object.entries(labels)
        .filter(([k]) => !k.endsWith('_photographs'))              // not photo fields
        .map(([k, label]) => [k, label, (data as any)[k]] as const)
        .filter(([_, __, v]) => typeof v === 'boolean');           // only booleans

    if (!items.length) return null;

    return (
        <div className="mb-6">
            <h4 className="text-xl font-bold text-gray-800 mb-3">{title}</h4>
            <div className="grid sm:grid-cols-2 gap-2">
                {items.map(([k, label, value]) => (
                    <div key={k} className="flex items-center justify-between rounded border px-3 py-2">
                        <span className="text-sm text-gray-800">{label}</span>
                        <span
                            className={`text-xs px-2 py-0.5 rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                        >
                            {value ? 'Yes' : 'No'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}


const isPhotoField = (k: string) => k.endsWith('_photographs');

// Make a photo-only initial state: { general_photographs: [], ... }
function makeInitialPhotoState(labels: Record<string, string>) {
    return Object.keys(labels).reduce<Record<string, string[]>>((acc, k) => {
        if (isPhotoField(k)) acc[k] = [];
        return acc;
    }, {});
}


const CenterManagement: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const [centers, setCenters] = useState<Center[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedCenter, setSelectedCenter] = useState<Center | null>(null);
    const [reviewComments, setReviewComments] = useState('');
    const [showAllApprovals, setShowAllApprovals] = useState(false);
    const [editingCenter, setEditingCenter] = useState<Center | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [mps, setMobalisationParterns] = useState<MobilisationPartner[]>([]);
    const [mpFilter, setMpFilter] = useState<number | ''>('');
    const [formData, setFormData] = useState<CenterFormData>(INITIAL_CENTER_FORM);



    const [selectedTab, setSelectedTab] = useState<"details" | "history">(
        "details"
    ); // State for managing tabs


    useEffect(() => {
        fetchCenters();
    }, [statusFilter, user, mpFilter]);


    useEffect(() => {
        if (user && user.role == "SYSTEM_ADMIN") {
            fetchMobalisationPartners();
        }
    }, [user]);

    // when modal opens (create or edit), set up formData
    // useEffect(() => {
    //     if (!showCreateModal || !editingCenter) return;

    //     const photoInit = makeInitialPhotoState(labels);
    //     const photoArrays = Object.fromEntries(
    //         Object.keys(photoInit).map(k => [
    //             k,
    //             Array.isArray((editingCenter as any)[k]) ? (editingCenter as any)[k] : []
    //         ])
    //     ) as Record<string, string[]>;

    //     setFormData(prev => ({
    //         // keep all required defaults already in prev
    //         ...prev,
    //         // overlay the center’s values (won’t remove prev defaults if a key is missing)
    //         ...(editingCenter as Partial<typeof prev>),
    //         // ensure photo arrays exist
    //         ...photoArrays,
    //     }) as typeof prev);
    //     console.log(formData)
    // }, [showCreateModal, editingCenter, labels]);

    const fetchCenters = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await centerApi.getCenters({
                page: 1,
                limit: 999999,
                status: statusFilter,
                mp_id: mpFilter || undefined
            })
            if (response.success && response.data) {
                setCenters(response.data);
            }
        } catch (error: any) {
            console.error('Failed to fetch centers:', error);
            if (error.response?.status === 401) {
                console.error('Authentication failed - user might not be logged in or session expired');
            }
            setError(error.response?.data?.message || "Failed to fetch centers.");
        } finally {
            setLoading(false);
        }
    };

    const fetchMobalisationPartners = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/partners');
            if (response.status == 200 && response.data) {
                setMobalisationParterns(response.data.data);
            } else {
                setError(response.statusText || 'Failed to fetch users');
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setError(null);
            setSubmitLoading(true);

            const payload = sanitizeCenterPayload(formData);

            if (!editingCenter) {
                await centerApi.createCenter(payload);
            }
            else {
                await centerApi.updateCenter(editingCenter.center_id, payload);
            }
            setSuccess(editingCenter ? "Center updated successfully." : "Center created successfully");
            setShowCreateModal(false);
            fetchCenters();
            resetForm();

        } catch (error: any) {
            setError(error.response?.data?.message || editingCenter ? "Failed to update the center." : "Failed to create the center.");
            console.error('Failed to create center:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSubmitForApproval = async (id: number) => {
        try {
            setError(null);
            setLoading(true);
            const response = await api.post(`/centers/${id}/submit`);

            if (response.data.success) {
                setSuccess("Center submitted for approval.")
                fetchCenters();
            }
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to submit center for approval.");
            console.error('Failed to submit center:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReviewCenter = async (centerId: number, action: "Approved" | "Rejected") => {
        try {
            setError(null);
            setLoading(true);

            await centerApi.reviewCenter(centerId, {
                action,
                comments: reviewComments,
            });
            setSuccess(`Center status has been updated to ${action}.`);
            setReviewComments("");
            setSelectedCenter(null);
            fetchCenters();
        } catch (error: any) {
            setError(error.response?.data?.message || `Failed while trying to update the center to ${action}.`);
            console.error("Error reviewing trainer:", error);
        } finally {
            setLoading(false);
        }
    };

    const openEditForm = (center: Center) => {
        setError(null);
        setSubmitLoading(false);
        // Updated to reflect the new, flattened fields
        setFormData({
            name: center.name,
            address: center.address,
            nearbyLandmark: center.nearbyLandmark || '',
            pincode: center.pincode,
            distanceFromCityCenter: center.distanceFromCityCenter,
            geoCoordinates: center.geoCoordinates || '',
            cctvFeedLink: center.cctvFeedLink || '',

            general_cleanPremises: center.general_cleanPremises || false,
            general_accessible: center.general_accessible || false,
            general_properCarpetArea: center.general_properCarpetArea || false,
            general_separateAreas: center.general_separateAreas || false,
            general_receptionDesk: center.general_receptionDesk || false,
            general_photographs: center.general_photographs || [],

            classroom_dedicatedClassrooms: center.classroom_dedicatedClassrooms || false,
            classroom_whiteboard: center.classroom_whiteboard || false,
            classroom_adequateChairs: center.classroom_adequateChairs || false,
            classroom_trainerTable: center.classroom_trainerTable || false,
            classroom_lightingAndVentilation: center.classroom_lightingAndVentilation || false,
            classroom_fansOrAC: center.classroom_fansOrAC || false,
            classroom_photographs: center.classroom_photographs || [],

            it_projectorOrTv: center.it_projectorOrTv || false,
            it_audioSystem: center.it_audioSystem || false,
            it_internetSpeed: center.it_internetSpeed || false,
            it_upsBackup: center.it_upsBackup || false,
            it_photographs: center.it_photographs || [],

            electrical_properWiring: center.electrical_properWiring || false,
            electrical_fireExtinguisher: center.electrical_fireExtinguisher || false,
            electrical_emergencyExitPlan: center.electrical_emergencyExitPlan || false,
            electrical_firstAidKit: center.electrical_firstAidKit || false,
            electrical_photographs: center.electrical_photographs || [],

            washrooms_separateToilets: center.washrooms_separateToilets || false,
            washrooms_regularCleaning: center.washrooms_regularCleaning || false,
            washrooms_handwashing: center.washrooms_handwashing || false,
            washrooms_photographs: center.washrooms_photographs || [],

            recordKeeping_attendanceSystem: center.recordKeeping_attendanceSystem || false,
            recordKeeping_cctvInstalled: center.recordKeeping_cctvInstalled || false,
            recordKeeping_cctvLink: center.recordKeeping_cctvLink || '',
            recordKeeping_photographs: center.recordKeeping_photographs || [],

            branding_brandingAtEntrance: center.branding_brandingAtEntrance || false,
            branding_posters: center.branding_posters || false,
            branding_photographs: center.branding_photographs || [],
        });
        setEditingCenter(center);
        setShowCreateModal(true);
    };

    const resetForm = () => {
        // Updated to reflect the new, flattened fields
        setFormData(INITIAL_CENTER_FORM);
        setEditingCenter(null);
        setShowCreateModal(false);
        setError(null);
        setSubmitLoading(false);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Draft':
                return <Clock className="w-4 h-4 text-gray-500" />;
            case 'Submitted':
                return <Clock className="w-4 h-4 text-blue-500" />;
            case 'UnderReview':
                return <Eye className="w-4 h-4 text-yellow-500" />;
            case 'Approved':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'Rejected':
                return <XCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft':
                return 'bg-gray-100 text-gray-800';
            case 'Submitted':
                return 'bg-blue-100 text-blue-800';
            case 'UnderReview':
                return 'bg-yellow-100 text-yellow-800';
            case 'Approved':
                return 'bg-green-100 text-green-800';
            case 'Rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredCenters = centers.filter(center =>
        // center.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        // center.address.toLowerCase().includes(searchTerm.toLowerCase()) || 
        // center.pincode.toLowerCase().includes(searchTerm.toLowerCase())
        `${center.name} ${center.address} ${center.pincode ?? ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleString(); // Adjust as needed for specific locale/format
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    const isModalOpen = showCreateModal || showViewModal;

    return (
        // className="space-y-6"
        <div >
            {/* Notifications */}
            {error && !isModalOpen && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Center Management</h1>
                    <p className="text-gray-600">Manage training centers and their approvals</p>
                </div>
                {user?.role == "MOBILISATION_PARTNER_ADMIN" && <button
                    onClick={() => { setError(null); setEditingCenter(null); setFormData(INITIAL_CENTER_FORM); setSubmitLoading(false); setShowCreateModal(true); }}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Center
                </button>}
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search centers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    {user?.role === 'SYSTEM_ADMIN' && (
                        <div className="sm:w-48">
                            <div className="relative">
                                {/* Optional: Add an icon here */}
                                <select
                                    id="mp-filter"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    value={mpFilter}
                                    onChange={e => setMpFilter(e.target.value ? Number(e.target.value) : '')}
                                >
                                    <option value="">All Partners</option>
                                    {mps.map(mp => (
                                        <option key={mp.mp_id} value={mp.mp_id}>
                                            {mp.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {user?.role == "MOBILISATION_PARTNER_ADMIN" && <div className="sm:w-48">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            <option value="Draft">Draft</option>
                            <option value="Submitted">Submitted</option>
                            {/* <option value="UnderReview">Under Review</option> */}
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>}
                    <button
                        onClick={fetchCenters}
                        className="btn-secondary flex items-center"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Centers Table */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-dashed border-indigo-500"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Center ID
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Address
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        MP
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Status
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCenters.length > 0 ? (
                                    filteredCenters.map((center) => (
                                        <tr key={center.center_id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {center.center_id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {center.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {center.address}, {center.pincode}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {center.mobilisation_partner?.name || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(center.status)}`}>
                                                    <span className="flex items-center">
                                                        {getStatusIcon(center.status)}
                                                        <span className="ml-1">{center.status}</span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedCenter(center);
                                                            setShowViewModal(true);
                                                        }}
                                                        className="text-indigo-600 hover:text-indigo-900 transition duration-150 ease-in-out"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>
                                                    {['Draft', 'Rejected'].includes(center.status) && user?.role && ['MOBILISATION_PARTNER_ADMIN', 'CENTER_ADMIN', 'PROJECT_MANAGER'].includes(user?.role) && (
                                                        <>
                                                            <button
                                                                onClick={() => openEditForm(center)}
                                                                className="text-primary-600 hover:text-primary-900 transition duration-150 ease-in-out"
                                                            >
                                                                <Edit className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleSubmitForApproval(center.center_id)}
                                                                className="text-green-600 hover:text-green-900 transition duration-150 ease-in-out"
                                                            >
                                                                <Send className="w-5 h-5" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No centers found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center pb-3 border-b">
                            <h3 className="text-2xl font-semibold text-gray-900">
                                {editingCenter ? "Edit Center" : "Create New Center"}
                            </h3>
                            <button
                                onClick={resetForm}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        {error && showCreateModal && (
                            <div className="mt-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                        <form
                            onSubmit={handleSubmit}
                            onChangeCapture={() => setError(null)}
                            className="mt-4 space-y-4"
                        >
                            {/* Basic Details */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2">Center Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        {labels.name}
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                        {labels.address}
                                    </label>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="nearbyLandmark" className="block text-sm font-medium text-gray-700">
                                        {labels.nearbyLandmark}
                                    </label>
                                    <input
                                        type="text"
                                        id="nearbyLandmark"
                                        name="nearbyLandmark"
                                        value={formData.nearbyLandmark}
                                        onChange={(e) => setFormData({ ...formData, nearbyLandmark: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="pincode" className="block text-sm font-medium text-gray-700">
                                        {labels.pincode}
                                    </label>
                                    <input
                                        type="text"
                                        id="pincode"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="distanceFromCityCenter" className="block text-sm font-medium text-gray-700">
                                        {labels.distanceFromCityCenter}
                                    </label>
                                    <select
                                        id="distanceFromCityCenter"
                                        name="distanceFromCityCenter"
                                        value={formData.distanceFromCityCenter}
                                        onChange={(e) => setFormData({ ...formData, distanceFromCityCenter: e.target.value as CenterFormData['distanceFromCityCenter'] })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                        required
                                    >
                                        <option value="ZERO_KM">0 Kms</option>
                                        <option value="ONE_TO_FIVE_KM">1-5 Kms</option>
                                        <option value="SIX_TO_TEN_KM">6-10 Kms</option>
                                <option value="ELEVEN_TO_TWENTY_KM">11-20 Kms</option>
                                <option value="BEYOND_TWENTY_KM">Beyond 20 Kms</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="geoCoordinates" className="block text-sm font-medium text-gray-700">
                                {labels.geoCoordinates}
                            </label>
                            <input
                                type="text"
                                id="geoCoordinates"
                                name="geoCoordinates"
                                value={formData.geoCoordinates}
                                onChange={(e) => setFormData({ ...formData, geoCoordinates: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                placeholder="e.g., 12.9716, 77.5946"
                            />
                        </div>
                        <div>
                            <label htmlFor="cctvFeedLink" className="block text-sm font-medium text-gray-700">
                                {labels.cctvFeedLink}
                            </label>
                            <input
                                type="url"
                                id="cctvFeedLink"
                                name="cctvFeedLink"
                                value={formData.cctvFeedLink}
                                onChange={(e) => setFormData({ ...formData, cctvFeedLink: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                placeholder="https://"
                            />
                        </div>
                    </div>
                            {/* General Infrastructure */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">General Infrastructure Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('general_') && key !== 'general_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="general_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.general_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="general_photographs"
                                        name="general_photographs"
                                        value={formData.general_photographs}
                                        onChange={(e) => setFormData({ ...formData, general_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.general_photographs}
                                        keys={formData.general_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, general_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Classroom Facilities */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">Classroom Facilities Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('classroom_') && key !== 'classroom_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="classroom_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.classroom_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="classroom_photographs"
                                        name="classroom_photographs"
                                        value={formData.classroom_photographs}
                                        onChange={(e) => setFormData({ ...formData, classroom_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.classroom_photographs}
                                        keys={formData.classroom_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, classroom_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* IT Infrastructure */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">IT Infrastructure Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('it_') && key !== 'it_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="it_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.it_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="it_photographs"
                                        name="it_photographs"
                                        value={formData.it_photographs}
                                        onChange={(e) => setFormData({ ...formData, it_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.it_photographs}
                                        keys={formData.it_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, it_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Electrical and Safety */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">Electrical and Safety Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('electrical_') && key !== 'electrical_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="electrical_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.electrical_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="electrical_photographs"
                                        name="electrical_photographs"
                                        value={formData.electrical_photographs}
                                        onChange={(e) => setFormData({ ...formData, electrical_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.electrical_photographs}
                                        keys={formData.electrical_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, electrical_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Washrooms and Hygiene */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">Washrooms and Hygiene Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('washrooms_') && key !== 'washrooms_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="washrooms_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.washrooms_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="washrooms_photographs"
                                        name="washrooms_photographs"
                                        value={formData.washrooms_photographs}
                                        onChange={(e) => setFormData({ ...formData, washrooms_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.washrooms_photographs}
                                        keys={formData.washrooms_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, washrooms_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Record Keeping and Display */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">Record Keeping and Display Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('recordKeeping_') && key !== 'recordKeeping_photographs' && key !== 'recordKeeping_cctvLink').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div>
                                    <label htmlFor="recordKeeping_cctvLink" className="block text-sm font-medium text-gray-700">
                                        {labels.recordKeeping_cctvLink}
                                    </label>
                                    <input
                                        type="text"
                                        id="recordKeeping_cctvLink"
                                        name="recordKeeping_cctvLink"
                                        value={formData.recordKeeping_cctvLink}
                                        onChange={(e) => setFormData({ ...formData, recordKeeping_cctvLink: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="recordKeeping_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.recordKeeping_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="recordKeeping_photographs"
                                        name="recordKeeping_photographs"
                                        value={formData.recordKeeping_photographs}
                                        onChange={(e) => setFormData({ ...formData, recordKeeping_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.recordKeeping_photographs}
                                        keys={formData.recordKeeping_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, recordKeeping_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Branding and Signage */}
                            <h4 className="text-lg font-bold text-gray-700 border-b pb-2 mt-6">Branding and Signage Checklist</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(labels).filter(key => key.startsWith('branding_') && key !== 'branding_photographs').map(key => (
                                    <div key={key} className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id={key}
                                                name={key}
                                                type="checkbox"
                                                checked={formData[key as keyof CenterFormData] as boolean}
                                                onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor={key} className="font-medium text-gray-700">
                                                {labels[key as keyof typeof labels]}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                <div className="md:col-span-2">
                                    <label htmlFor="branding_photographs" className="block text-sm font-medium text-gray-700">
                                        {labels.branding_photographs}
                                    </label>
                                    {/* <input
                                        type="text"
                                        id="branding_photographs"
                                        name="branding_photographs"
                                        value={formData.branding_photographs}
                                        onChange={(e) => setFormData({ ...formData, branding_photographs: e.target.value })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    /> */}
                                    {/* <MultiImageField
                                        label={labels.branding_photographs}
                                        keys={formData.branding_photographs}
                                        onChange={(next) => setFormData(p => ({ ...p, branding_photographs: next }))}
                                    /> */}
                                </div>
                            </div>
                            {/* Auto-render every *_photographs field */}
                            {Object.entries(labels)
                                .filter(([field]) => isPhotoField(field))
                                .map(([field, label]) => {
                                    const keys = (formData as any)[field] as string[] | undefined;
                                    return (
                                        <MultiImageField
                                            key={field}
                                            label={label.replace('Upload Photograph/Photographs ', '').replace(/[()]/g, '')}
                                            keys={Array.isArray(keys) ? keys : []}
                                            onChange={(next) => setFormData(p => ({ ...p, [field]: next }))}
                                        />
                                    );
                                })}

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    disabled={submitLoading}
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showCreateModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showCreateModal
                                            ? (editingCenter ? "Saving..." : "Creating...")
                                            : (editingCenter ? "Update Center" : "Create Center")}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* View Modal */}
            {showViewModal && selectedCenter && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                        <div className="relative p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full overflow-hidden">
                            {/* Modal Header */}
                            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                <h3 className="text-2xl font-bold text-gray-900">Center Name: {selectedCenter.name}</h3>
                                <button
                                    className="text-gray-400 hover:text-gray-600"
                                    onClick={() => {
                                        setSelectedCenter(null);
                                        setShowViewModal(false);
                                    }}
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6">
                                {/* Tab Navigation */}
                                <div className="border-b border-gray-200 mb-6">
                                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                                        <button
                                            onClick={() => setSelectedTab('details')}
                                            className={`${selectedTab === 'details'
                                                ? 'border-primary-500 text-primary-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                                        >
                                            Center Details
                                        </button>
                                        <button
                                            onClick={() => setSelectedTab('history')}
                                            className={`${selectedTab === 'history'
                                                ? 'border-primary-500 text-primary-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                                        >
                                            Review History
                                        </button>
                                    </nav>
                                </div>

                                {/* Tab Content */}
                                {selectedTab === 'details' && (
                                    <div className="space-y-8">
                                        {/* Basic Details Section */}
                                        <div>
                                            <h4 className="text-xl font-bold text-gray-800 mb-4">Basic Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.name}</span>
                                                    <span className="mt-1 text-base text-gray-900">{selectedCenter.name}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.address}</span>
                                                    <span className="mt-1 text-base text-gray-900">{selectedCenter.address}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.nearbyLandmark}</span>
                                                    <span className="mt-1 text-base text-gray-900">{selectedCenter.nearbyLandmark || 'N/A'}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.pincode}</span>
                                                    <span className="mt-1 text-base text-gray-900">{selectedCenter.pincode}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">
                                                        {labels.distanceFromCityCenter}
                                                    </span>
                                                    <span className="mt-1 text-base text-gray-900">
                                                        {selectedCenter.distanceFromCityCenter === 'ZERO_KM' ? '0 Kms' :
                                                            selectedCenter.distanceFromCityCenter === 'ONE_TO_FIVE_KM' ? '1-5 Kms' :
                                                                selectedCenter.distanceFromCityCenter === 'SIX_TO_TEN_KM' ? '6-10 Kms' :
                                                                    selectedCenter.distanceFromCityCenter === 'ELEVEN_TO_TWENTY_KM' ? '11-20 Kms' :
                                                                        selectedCenter.distanceFromCityCenter === 'BEYOND_TWENTY_KM' ? 'Beyond 20 Kms' :
                                                                            '—'}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.geoCoordinates}</span>
                                                    <span className="mt-1 text-base text-gray-900">{selectedCenter.geoCoordinates || 'N/A'}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-500">{labels.cctvFeedLink}</span>
                                                    {selectedCenter.cctvFeedLink ? (
                                                        <a
                                                            href={selectedCenter.cctvFeedLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mt-1 text-base text-primary-600 hover:text-primary-800 break-words"
                                                        >
                                                            {selectedCenter.cctvFeedLink}
                                                        </a>
                                                    ) : (
                                                        <span className="mt-1 text-base text-gray-900">N/A</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>


                                        {/* Dynamic Checklists and Photos */}
                                        {/* {Object.entries(labels).map(([key, label]) => {
                                            if (
                                                key.startsWith('general_') ||
                                                key.startsWith('classroom_') ||
                                                key.startsWith('it_') ||
                                                key.startsWith('electrical_') ||
                                                key.startsWith('washrooms_') ||
                                                key.startsWith('recordKeeping_') ||
                                                key.startsWith('branding_')
                                            ) {
                                                const value = (selectedCenter as any)[key];
                                                // Handle photograph fields
                                                if (key.endsWith('_photographs')) {
                                                    return (
                                                        <div key={key}>
                                                            <h4 className="text-xl font-bold text-gray-800 mb-4">{label.replace('Upload Photograph/Photographs ', '').replace(')', '').replace('(', '')}</h4>
                                                            {value ? (
                                                                <ImageGridSigned keys={value as string[]}  title={label} />
                                                                // <img src={value} alt={label} className="w-full h-auto rounded-lg shadow-md max-w-sm" />
                                                            ) : (
                                                                <p className="text-gray-500">No photograph provided.</p>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Handle boolean checklist items
                                                if (typeof value === 'boolean') {
                                                    return (
                                                        <div key={key} className="flex items-center gap-2">
                                                            {value ? (
                                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                                            ) : (
                                                                <XCircle className="w-5 h-5 text-red-500" />
                                                            )}
                                                            <span className="text-base text-gray-700">{label}</span>
                                                        </div>
                                                    );
                                                }

                                                // Handle other text fields
                                                if (typeof value === 'string' && !key.includes('_photographs')) {
                                                    return (
                                                        <div key={key} className="flex flex-col">
                                                            <span className="text-sm font-medium text-gray-500">{label}</span>
                                                            <span className="mt-1 text-base text-gray-900">{value || 'N/A'}</span>
                                                        </div>
                                                    );
                                                }
                                            }
                                            return null;
                                        })} */}

                                        {/* Boolean checklist (auto-detects all boolean fields) */}
                                        <BooleanChecklist data={selectedCenter as any} labels={labels} title="Compliance Checklist" />


                                        {/* Automatically render all *_photographs fields */}
                                        {Object.entries(labels)
                                            .filter(([field]) => field.endsWith('_photographs'))
                                            .map(([field, label]) => {
                                                const keys = (selectedCenter as any)[field] as string[] | undefined;
                                                return (
                                                    <div key={field} className="mb-6">
                                                        <h4 className="text-xl font-bold text-gray-800 mb-4">
                                                            {label.replace('Upload Photograph/Photographs ', '')
                                                                .replace(/[()]/g, '')}
                                                        </h4>
                                                        {Array.isArray(keys) && keys.length > 0 ? (
                                                            <ImageGridSigned keys={keys} title={label} />
                                                        ) : (
                                                            <p className="text-gray-500">No photographs provided.</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {/* Review History Tab Content */}
                                {selectedTab === "history" && (
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">
                                            Approval History
                                        </h4>
                                        {selectedCenter.approval_history &&
                                            selectedCenter.approval_history.length > 0 ? (
                                            <div className="divide-y divide-gray-200">
                                                {selectedCenter.approval_history
                                                    .slice(
                                                        0,
                                                        showAllApprovals
                                                            ? selectedCenter.approval_history.length
                                                            : 3
                                                    )
                                                    .map((entry, index) => (
                                                        <div key={index} className="py-3">
                                                            <div className="flex items-center justify-between">
                                                                <span
                                                                    className={`font-medium ${entry.action === "Approved"
                                                                        ? "text-green-600"
                                                                        : entry.action === "Rejected"
                                                                            ? "text-red-600"
                                                                            : "text-gray-800"
                                                                        }`}
                                                                >
                                                                    {entry.action}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {formatDateTime(entry.reviewed_at)}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-700 mt-1">
                                                                {entry.comments || "No comments provided."}
                                                            </p>
                                                        </div>
                                                    ))}
                                                {selectedCenter.approval_history.length > 3 && (
                                                    <div className="text-center pt-3">
                                                        <button
                                                            onClick={() => setShowAllApprovals(!showAllApprovals)}
                                                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                                        >
                                                            {showAllApprovals ? "Show Less" : "Show All"}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-gray-500 text-sm">
                                                No approval history available for this trainer.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Review Action section for System Admins */}
                            {user?.role === 'SYSTEM_ADMIN' && selectedCenter.status === 'Submitted' && (
                                <div className="p-6 border-t border-gray-200">
                                    <h4 className="text-xl font-bold text-gray-800 mb-4">Review Center</h4>
                                    <textarea
                                        className="w-full h-24 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="Add your review comments..."
                                        value={reviewComments}
                                        onChange={(e) => setReviewComments(e.target.value)}
                                    ></textarea>
                                    <div className="mt-4 flex justify-end space-x-3">
                                        <button
                                            onClick={() => handleReviewCenter(selectedCenter.center_id, "Approved")}
                                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                            disabled={loading}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleReviewCenter(selectedCenter.center_id, "Rejected")}
                                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            disabled={loading}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </ModalPortal>
            )}

        </div>
    );
};

export default CenterManagement;
